import { useNavigate } from 'react-router-dom';
import type { ScrapbookSummary } from '@shared/types';
import { formatRelativeTime } from '@shared/utils';

interface ScrapbookCardProps {
  scrapbook: ScrapbookSummary;
  onDelete: (id: string) => void;
  onShare: (id: string) => void;
}

export function ScrapbookCard({ scrapbook, onDelete, onShare }: ScrapbookCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/editor/${scrapbook.id}`);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare(scrapbook.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(scrapbook.id);
  };

  return (
    <article
      className="scrapbook-card"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`Open ${scrapbook.title}`}
    >
      {/* Thumbnail */}
      <div className="scrapbook-card-thumbnail">
        {scrapbook.thumbnailUrl ? (
          <img
            src={scrapbook.thumbnailUrl}
            alt=""
            className="scrapbook-card-thumbnail-image"
          />
        ) : (
          <div className="scrapbook-card-thumbnail-placeholder">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="8"
                y="8"
                width="32"
                height="32"
                rx="4"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M16 28L22 22L26 26L32 20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="18" cy="18" r="2" fill="currentColor" />
            </svg>
          </div>
        )}

        {/* Hover Actions */}
        <div className="scrapbook-card-actions">
          <button
            className="scrapbook-card-action"
            onClick={handleShare}
            aria-label="Share scrapbook"
            title="Share"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 5.5C13.1046 5.5 14 4.60457 14 3.5C14 2.39543 13.1046 1.5 12 1.5C10.8954 1.5 10 2.39543 10 3.5C10 4.60457 10.8954 5.5 12 5.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M4 10C5.10457 10 6 9.10457 6 8C6 6.89543 5.10457 6 4 6C2.89543 6 2 6.89543 2 8C2 9.10457 2.89543 10 4 10Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M12 14.5C13.1046 14.5 14 13.6046 14 12.5C14 11.3954 13.1046 10.5 12 10.5C10.8954 10.5 10 11.3954 10 12.5C10 13.6046 10.8954 14.5 12 14.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M5.72 9.12L10.28 11.38"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M10.28 4.62L5.72 6.88"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </button>
          <button
            className="scrapbook-card-action scrapbook-card-action-danger"
            onClick={handleDelete}
            aria-label="Delete scrapbook"
            title="Delete"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2 4H14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.66667 7.33333V11.3333"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M9.33333 7.33333V11.3333"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="scrapbook-card-info">
        <h3 className="scrapbook-card-title">{scrapbook.title}</h3>
        <p className="scrapbook-card-meta">
          <span className="scrapbook-card-pages">
            {scrapbook.pageCount} {scrapbook.pageCount === 1 ? 'page' : 'pages'}
          </span>
          <span className="scrapbook-card-dot" aria-hidden="true">
            ·
          </span>
          <span className="scrapbook-card-updated">
            {formatRelativeTime(scrapbook.updatedAt)}
          </span>
        </p>
      </div>
    </article>
  );
}
